import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../Common/Configurations/AuthContext';
import Header from '../../../Common/Layout/Header';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import moment from 'moment';
import config from '../../../Common/Configurations/APIConfig';
import apiService from "../../../Common/Configurations/APIService";
import styles from '../FlightDetails/FlightDetails2.module.css'; // Reusing the established design
import { 
    RiCheckboxCircleLine, RiPlaneLine, RiUserLine, RiTicketLine, 
    RiFileTextLine, RiPrinterLine, RiArrowLeftLine, RiTimeLine, RiInformationLine 
} from "react-icons/ri";

const FlightBookingConfirmation2 = ({ mode, onNavigate, templateId, params: builderParams, styleConfig, branding, companyId }) => {
    const urlParams = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { user, loading } = useAuth();

    // DYNAMIC CSS VARIABLES
    const themeVariables = {
        "--primary-color": styleConfig?.primary || "#c9a84c",
        "--secondary-color": styleConfig?.secondary || "#e8cc7a",
    };

    const [pageLoading, setPageLoading] = useState(false);
    const [bookingDetails, setBookingDetails] = useState({});
    const [travellers, setTravellers] = useState([]);
    const [itinerary, setItinerary] = useState([]);

    const formatTime = (dt) => dt ? moment(dt).format("HH:mm") : "--:--";
    const formatDate = (dt) => dt ? moment(dt).format("DD MMM YY") : "---";

    function getTimeFromMins(mins) {
        var h = mins / 60 | 0, m = mins % 60 | 0;
        return h + "h " + m + "m";
    }

    useEffect(() => {
        // 🚀 3-MODE DATA LOADING
        if (mode === "builder" || mode === "preview") {
            // Use mock data for design/preview
            const mockData = builderParams?.bookingData || state?.bookingData || state?.flightBookingResponse || {};
            setBookingDetails(mockData);
            setItinerary(mockData.dcFlightLegs || []);
            setTravellers(mockData.travellerInfos || []);
            setPageLoading(false);
        } else {
            // Live Mode: Fetch from real API
            if (urlParams.flightBookingId) {
                setPageLoading(true);
                apiService.getAll(config.GETFLIGHTBOOKINGDETAILS + "/" + urlParams.flightBookingId)
                    .then((res) => {
                        setBookingDetails(res);
                        setItinerary(res.dcFlightLegs || []);
                        setTravellers(res.travellerInfos || []);
                        setPageLoading(false);
                    })
                    .catch((err) => {
                        console.error("Error fetching booking details", err);
                        setPageLoading(false);
                    });
            }
        }
    }, [urlParams.flightBookingId, mode, builderParams, state]);

    const handleNewSearch = () => {
        if (mode === "builder") onNavigate({ name: "flight-search" });
        else if (mode === "preview") navigate(`/template-preview/${templateId}/flight-search`);
        else navigate("/template/flight/search");
    };

    if (loading || pageLoading) return <div className={styles.page} style={themeVariables}><p style={{color: '#fff', padding: 20}}>Loading booking details...</p></div>;
    if (!user && mode === "live") return null;

    const isConfirmed = bookingDetails.bookingStatus === "CONFIRMED";

    return (
        <div className={styles.page} style={themeVariables}>
           
            {/* Top Action Bar */}
            <div className={styles.topBar}>
                <div className={styles.topBarInner}>
                    <div className={styles.steps}>
                        <div className={styles.step}>
                            <div className={`${styles.stepNum} ${styles.stepNumDone}`}>✓</div>
                            <span className={`${styles.stepLabel} ${styles.stepLabelDone}`}>Details</span>
                        </div>
                        <div className={styles.stepArrow} />
                        <div className={styles.step}>
                            <div className={`${styles.stepNum} ${styles.stepNumDone}`}>✓</div>
                            <span className={`${styles.stepLabel} ${styles.stepLabelDone}`}>Payment</span>
                        </div>
                        <div className={styles.stepArrow} />
                        <div className={styles.step}>
                            <div className={`${styles.stepNum} ${styles.stepNumActive}`}>3</div>
                            <span className={`${styles.stepLabel} ${styles.stepLabelActive}`}>Confirmation</span>
                        </div>
                    </div>
                    <div className={styles.topBarSpacer} />
                    <button className={styles.backBtn} onClick={handleNewSearch}><RiArrowLeftLine /> New Search</button>
                </div>
            </div>

            <div className={styles.main} style={{ gridTemplateColumns: '1fr' }}>
                <div className={styles.leftCol}>
                    
                    {/* Success Message */}
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <RiCheckboxCircleLine style={{ fontSize: '4.5rem', color: '#4ade80', marginBottom: '15px' }} />
                        <h1 style={{ fontSize: '2.2rem', fontWeight: 800, marginBottom: '10px' }}>Booking {bookingDetails.bookingStatus || 'Confirmed'}!</h1>
                        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Your PNR is <strong style={{ color: 'var(--primary-color)' }}>{bookingDetails.pnr || 'TBD'}</strong>. A confirmation has been sent to your email.</p>
                    </div>

                    {/* Booking Reference Card */}
                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionHead}>
                            <RiTicketLine className={styles.sectionHeadIcon} />
                            <span className={styles.sectionTitle}>Ticket Information</span>
                            {isConfirmed && (
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                                    <button className={styles.detailsLink}><RiPrinterLine /> Print</button>
                                    <button className={styles.detailsLink}><RiFileTextLine /> Voucher</button>
                                </div>
                            )}
                        </div>
                        <div className={styles.sectionBody}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                                <div>
                                    <small style={{ color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 800 }}>PNR Number</small>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-color)' }}>{bookingDetails.pnr || 'N/A'}</div>
                                </div>
                                <div>
                                    <small style={{ color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 800 }}>Booking Number</small>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{bookingDetails.bookingNo || 'N/A'}</div>
                                </div>
                                <div>
                                    <small style={{ color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 800 }}>Class</small>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{bookingDetails.cabinClass || 'Economy'}</div>
                                </div>
                                <div>
                                    <small style={{ color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 800 }}>Status</small>
                                    <div style={{ color: isConfirmed ? '#4ade80' : '#facc15', fontWeight: 800 }}>● {bookingDetails.bookingStatus}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Itinerary Summary */}
                    <div className={styles.sectionHead} style={{ border: 'none', paddingLeft: 0 }}>
                        <RiPlaneLine className={styles.sectionHeadIcon} />
                        <span className={styles.sectionTitle}>Flight Itinerary</span>
                    </div>
                    {itinerary.length > 0 && itinerary.map((it, idx) => {
    const flightLeg = it?.flightLeg || it;

    return flightLeg?.dcDisplayLegs?.map((leg, lIdx) => (
        <div className={styles.flightCard} key={`${idx}-${lIdx}`}>
            <div className={styles.flightCardBody}>
                <div className={styles.airlineBlock}>
                    <div className={styles.airlineLogo}>
                        <img
                            src={`/assets/images/airlinelogos/${leg.airlineData.airlineCode}.png`}
                            className={styles.airlineLogoImg}
                            alt="Logo"
                        />
                    </div>
                    <div>
                        <div className={styles.airlineName}>{leg?.airlineData?.airline}</div>
                        <div className={styles.flightNum}>
                            {leg?.airlineData?.airlineCode}-{leg?.airlineData?.flightNumber}
                        </div>
                    </div>
                </div>

                <div className={styles.routeTrack}>
                    <div className={styles.timeBlock}>
                        <div className={styles.timeMain}>{formatTime(leg.startSegment.departureDateTime)}</div>
                        <div className={styles.timeApt}>{leg.startSegment.airportCode}</div>
                    </div>

                    <div className={styles.durationBlock}>
                        <div className={styles.durationText}>{getTimeFromMins(leg.totalDuration)}</div>
                        <div className={styles.trackLine}>
                            <div className={styles.trackDot}/>
                            <div className={styles.trackBar}/>
                            <div className={styles.trackDot}/>
                        </div>
                        <span className={styles.stopBadge}>
                            {leg.stops === 0 ? 'Non-Stop' : `${leg.stops} Stop(s)`}
                        </span>
                    </div>

                    <div className={styles.timeBlock}>
                        <div className={styles.timeMain}>{formatTime(leg.endSegment.arrivalDateTime)}</div>
                        <div className={styles.timeApt}>{leg.endSegment.airportCode}</div>
                    </div>
                </div>
            </div>
        </div>
    ));
})}

                    {/* Traveller Details Summary */}
                    <div className={styles.sectionBlock} style={{ marginTop: '20px' }}>
                        <div className={styles.sectionHead}>
                            <RiUserLine className={styles.sectionHeadIcon} />
                            <span className={styles.sectionTitle}>Traveller Details</span>
                        </div>
                        <div className={styles.sectionBody}>
                            <table className={styles.baggageTable}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Passenger Name</th>
                                        <th>Gender</th>
                                        <th>Type</th>
                                        <th>Seat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {travellers.map((t, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: 'left' }}><strong>{t.title} {t.firstName} {t.lastName}</strong></td>
                                            <td>{t.gender}</td>
                                            <td>{t.travelerType}</td>
                                            <td><span className={styles.stepNum} style={{ margin: '0 auto', background: 'rgba(255,255,255,0.05)' }}>{index + 1}A</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className={styles.actionBar}>
                        <button className={styles.btnPrimary} onClick={handleNewSearch}>Make Another Booking</button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default FlightBookingConfirmation2;